import type { TableOrderDraftPayload } from 'api/diningTables'
import localforage from 'localforage'
import {
  normalizePaymentLines as normalizeStoredPaymentLines,
  resolveOfflineScope,
  scopedPosKey,
  type OfflineCacheScope,
} from './posStore'

const idb = localforage.createInstance({
  name: 'erp-pos',
  storeName: 'pos',
})

const BASE_QUEUE = 'pos_table_ops_queue'
const BASE_FAILED = 'pos_failed_table_ops'
const BASE_META = 'pos_table_local_meta'
const BASE_CHECKOUT = 'pos_table_checkout_done'
const BASE_LOCAL_GROUPS = 'pos_table_local_groups'

export type LocalMergedTableSource = {
  id: string
  name: string
  capacity?: number
}

export type LocalTableGroupsRecord = {
  /** source table id → primary target table id */
  groupedInto: Record<string, string>
  /** target table id → merged-in tables for card title/UI */
  linkedSourcesByTarget: Record<string, LocalMergedTableSource[]>
}

export type TableCheckoutTombstone = {
  completedAt: string
  saleId?: number | string | null
  invoiceNumber?: string | null
}

export type TableOpStatus = 'pending' | 'syncing' | 'synced' | 'failed' | 'conflict'

export type TableSettlePayload = {
  price_defining_options_version?: number
  payment_type:
    | 'cash'
    | 'card'
    | 'credit'
    | 'layaway'
    | 'split'
    | 'wallet'
    | 'electronic_wallet'
    | 'instapay'
    | 'bank_transfer'
    | 'payment_gateway'
  paid: number
  cash_tendered?: number
  payment_lines?: {
    financial_account_id?: string | null
    vault_id?: string | null
    amount: number
    payment_method?: 'cash' | 'card' | 'wallet' | 'electronic_wallet' | 'instapay' | 'bank_transfer' | 'payment_gateway'
    reference?: string | null
    client_line_id?: string
  }[] | null
  customer_id?: number | null
  loyalty_points_redeemed?: number
  loyalty_discount?: number
  discount?: number
  cart_discount?: number
  promotion_discount?: number
  coupon_id?: string | null
  coupon_discount?: number
  service_charge?: number
  notes?: string | null
  layaway_terms?: Record<string, unknown> | null
  client_op_id?: string
  pos_register_id?: string | null
  register_session_id?: string | null
}

type TableOpBase = {
  id: string
  status: TableOpStatus
  enqueued_at: string
  retry_count?: number
  last_error?: string | null
  branch_id?: string | null
}

export type TableDraftOp = TableOpBase & {
  op: 'draft'
  table_id: string
  payload: TableOrderDraftPayload
  client_op_id?: string
  /** Set when draft was queued while offline (flush uses fresh meta / null expected). */
  enqueued_while_offline?: boolean
}

export type EnqueueTableDraftOptions = {
  enqueuedWhileOffline?: boolean
}

export type TableSettleOp = TableOpBase & {
  op: 'settle'
  table_id: string
  payload: TableSettlePayload
  after_draft_id?: string
}

export type TableTransferOp = TableOpBase & {
  op: 'transfer'
  source_id: string
  target_id: string
  enqueued_while_offline?: boolean
}

export type TableMergeOp = TableOpBase & {
  op: 'merge'
  source_id: string
  target_id: string
  enqueued_while_offline?: boolean
}

export type TableUnmergeOp = TableOpBase & {
  op: 'unmerge'
  target_id: string
  source_id: string
}

export type TableReleaseOp = TableOpBase & {
  op: 'release'
  table_id: string
}

export type TableOp =
  | TableDraftOp
  | TableSettleOp
  | TableTransferOp
  | TableMergeOp
  | TableUnmergeOp
  | TableReleaseOp

export type TableLocalMeta = {
  orderId?: number | string | null
  draftGeneration?: number | null
  saleUpdatedAt?: string | null
  cartRevision?: number | null
  printSequence?: number | string | null
  invoiceNumber?: string | null
}

export type TableLocalMetaRecord = Record<string, TableLocalMeta>

export interface FailedTableOp {
  op: TableOp
  reason: string
  failedAt: string
  lastAttemptAt?: string
  retryCount: number
}

function queueKey(scope: OfflineCacheScope = {}): string {
  return scopedPosKey(BASE_QUEUE, scope, 'userBranch')
}

function failedKey(scope: OfflineCacheScope = {}): string {
  return scopedPosKey(BASE_FAILED, scope, 'userBranch')
}

function metaKey(scope: OfflineCacheScope = {}): string {
  return scopedPosKey(BASE_META, scope, 'userBranch')
}

function checkoutKey(scope: OfflineCacheScope = {}): string {
  return scopedPosKey(BASE_CHECKOUT, scope, 'userBranch')
}

function localGroupsKey(scope: OfflineCacheScope = {}): string {
  return scopedPosKey(BASE_LOCAL_GROUPS, scope, 'userBranch')
}

function dispatchTableGroupsUpdated(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pos:table-groups-updated'))
  }
}

export async function getLocalTableGroupsRecord(
  scope: OfflineCacheScope = {},
): Promise<LocalTableGroupsRecord> {
  const raw = await idb.getItem<LocalTableGroupsRecord>(localGroupsKey(scope))
  if (!raw || typeof raw !== 'object') {
    return { groupedInto: {}, linkedSourcesByTarget: {} }
  }
  return {
    groupedInto: raw.groupedInto && typeof raw.groupedInto === 'object' ? raw.groupedInto : {},
    linkedSourcesByTarget:
      raw.linkedSourcesByTarget && typeof raw.linkedSourcesByTarget === 'object'
        ? raw.linkedSourcesByTarget
        : {},
  }
}

async function saveLocalTableGroupsRecord(
  rec: LocalTableGroupsRecord,
  scope: OfflineCacheScope = {},
): Promise<void> {
  await idb.setItem(localGroupsKey(scope), rec)
  dispatchTableGroupsUpdated()
}

/** Persist offline merge so the tables grid hides source and shows grouped target. */
export async function recordLocalTableMerge(
  sourceId: string,
  targetId: string,
  source: LocalMergedTableSource,
  scope: OfflineCacheScope = {},
): Promise<void> {
  const rec = await getLocalTableGroupsRecord(scope)
  for (const [sid, tid] of Object.entries(rec.groupedInto)) {
    if (tid === sourceId) rec.groupedInto[sid] = targetId
  }
  rec.groupedInto[sourceId] = targetId
  const list = rec.linkedSourcesByTarget[targetId] ?? []
  if (!list.some((e) => e.id === sourceId)) {
    rec.linkedSourcesByTarget[targetId] = [...list, source]
  }
  await saveLocalTableGroupsRecord(rec, scope)
}

export async function clearLocalTableMergeForSource(
  sourceId: string,
  scope: OfflineCacheScope = {},
): Promise<void> {
  const rec = await getLocalTableGroupsRecord(scope)
  const targetId = rec.groupedInto[sourceId]
  if (!targetId) return
  delete rec.groupedInto[sourceId]
  const list = rec.linkedSourcesByTarget[targetId]
  if (list) {
    rec.linkedSourcesByTarget[targetId] = list.filter((e) => e.id !== sourceId)
    if (rec.linkedSourcesByTarget[targetId].length === 0) {
      delete rec.linkedSourcesByTarget[targetId]
    }
  }
  await saveLocalTableGroupsRecord(rec, scope)
}

/** Overlay offline merge grouping onto cached/API table rows. */
export function applyLocalTableGroupsToTables<T extends {
  id: string
  grouped_into_table_id?: string | null
  linked_table_sources?: LocalMergedTableSource[] | null
}>(tables: T[], groups: LocalTableGroupsRecord): T[] {
  return tables.map((tb) => {
    const groupedInto = groups.groupedInto[tb.id] ?? tb.grouped_into_table_id ?? null
    const extra = groups.linkedSourcesByTarget[tb.id] ?? []
    const existing = tb.linked_table_sources ?? []
    const linkedMap = new Map<string, LocalMergedTableSource>()
    for (const e of existing) linkedMap.set(e.id, e)
    for (const e of extra) linkedMap.set(e.id, e)
    return {
      ...tb,
      grouped_into_table_id: groupedInto,
      linked_table_sources: linkedMap.size > 0 ? Array.from(linkedMap.values()) : null,
    }
  })
}

function newOpId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `tbl-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function enrichOp<T extends TableOp>(op: T): T {
  const scope = resolveOfflineScope({ branchId: op.branch_id ?? undefined })
  return {
    ...op,
    branch_id: op.branch_id ?? scope.branchId,
    status: op.status ?? 'pending',
    enqueued_at: op.enqueued_at ?? new Date().toISOString(),
    retry_count: op.retry_count ?? 0,
    last_error: op.last_error ?? null,
  }
}

export async function getTableOpsQueue(scope: OfflineCacheScope = {}): Promise<TableOp[]> {
  const raw = await idb.getItem<TableOp[]>(queueKey(scope))
  if (!Array.isArray(raw)) return []
  return raw.map((op) => {
    if (op.op !== 'settle') return op
    return {
      ...op,
      payload: {
        ...op.payload,
        payment_lines: normalizePaymentLines(op.payload.payment_lines),
      },
    }
  })
}

function normalizePaymentLines(
 lines: TableSettlePayload['payment_lines'],
): TableSettlePayload['payment_lines'] {
 return normalizeStoredPaymentLines(lines) as TableSettlePayload['payment_lines']
}

async function saveTableOpsQueue(queue: TableOp[], scope: OfflineCacheScope = {}): Promise<void> {
  await idb.setItem(queueKey(scope), queue)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pos:table-ops-updated'))
  }
}

export async function getTableLocalMetaRecord(scope: OfflineCacheScope = {}): Promise<TableLocalMetaRecord> {
  const raw = await idb.getItem<TableLocalMetaRecord>(metaKey(scope))
  return raw && typeof raw === 'object' ? raw : {}
}

export async function getTableLocalMeta(
  tableId: string,
  scope: OfflineCacheScope = {},
): Promise<TableLocalMeta | null> {
  const rec = await getTableLocalMetaRecord(scope)
  return rec[tableId] ?? null
}

export async function setTableLocalMeta(
  tableId: string,
  meta: TableLocalMeta | null,
  scope: OfflineCacheScope = {},
): Promise<void> {
  const rec = await getTableLocalMetaRecord(scope)
  if (meta == null) {
    delete rec[tableId]
  } else {
    rec[tableId] = { ...rec[tableId], ...meta }
  }
  await idb.setItem(metaKey(scope), rec)
}

export async function clearTableLocalMeta(tableId: string, scope: OfflineCacheScope = {}): Promise<void> {
  await setTableLocalMeta(tableId, null, scope)
}

export async function getTableCheckoutRecord(
  scope: OfflineCacheScope = {},
): Promise<Record<string, TableCheckoutTombstone>> {
  const raw = await idb.getItem<Record<string, TableCheckoutTombstone>>(checkoutKey(scope))
  return raw && typeof raw === 'object' ? raw : {}
}

export async function getTableCheckoutTombstone(
  tableId: string,
  scope: OfflineCacheScope = {},
): Promise<TableCheckoutTombstone | null> {
  const rec = await getTableCheckoutRecord(scope)
  return rec[tableId] ?? null
}

export async function setTableCheckoutTombstone(
  tableId: string,
  tombstone: TableCheckoutTombstone,
  scope: OfflineCacheScope = {},
): Promise<void> {
  const rec = await getTableCheckoutRecord(scope)
  rec[tableId] = tombstone
  await idb.setItem(checkoutKey(scope), rec)
}

export async function clearTableCheckoutTombstone(
  tableId: string,
  scope: OfflineCacheScope = {},
): Promise<void> {
  const rec = await getTableCheckoutRecord(scope)
  if (!rec[tableId]) return
  delete rec[tableId]
  await idb.setItem(checkoutKey(scope), rec)
}

/** Remove stale pending table ops after checkout (optional: keep settle for offline flush). */
export async function purgePendingTableOpsForTable(
  tableId: string,
  options?: { keepSettle?: boolean },
  scope: OfflineCacheScope = {},
): Promise<number> {
  const queue = await getTableOpsQueue(scope)
  let removed = 0
  const next = queue.filter((op) => {
    if (op.status !== 'pending' && op.status !== 'syncing') return true
    const tableOp =
      op.op === 'draft' || op.op === 'settle' || op.op === 'release'
    if (!tableOp || op.table_id !== tableId) return true
    if (options?.keepSettle && op.op === 'settle') return true
    removed += 1
    return false
  })
  if (removed > 0) await saveTableOpsQueue(next, scope)
  return removed
}

/** Replace the last pending draft for the same table, or append. */
export async function enqueueTableDraft(
  tableId: string,
  payload: TableOrderDraftPayload,
  scope: OfflineCacheScope = {},
  options?: EnqueueTableDraftOptions,
): Promise<TableDraftOp> {
  if (payload.items.length > 0) {
    await clearTableCheckoutTombstone(tableId, scope)
  }
  const queue = await getTableOpsQueue(scope)
  const id = newOpId()
  const entry = enrichOp<TableDraftOp>({
    op: 'draft',
    id,
    table_id: tableId,
    payload,
    client_op_id: id,
    enqueued_while_offline: options?.enqueuedWhileOffline === true,
    status: 'pending',
    enqueued_at: new Date().toISOString(),
  })

  let replaced = false
  for (let i = queue.length - 1; i >= 0; i--) {
    const item = queue[i]
    if (item.op === 'draft' && item.table_id === tableId && item.status === 'pending') {
      queue[i] = {
        ...entry,
        id: item.id,
        client_op_id: item.client_op_id ?? item.id,
        enqueued_while_offline: entry.enqueued_while_offline ?? item.enqueued_while_offline,
      }
      replaced = true
      break
    }
  }
  if (!replaced) queue.push(entry)

  await saveTableOpsQueue(queue, scope)
  const saved = await getTableOpsQueue(scope)
  const found = [...saved].reverse().find(
    (o): o is TableDraftOp => o.op === 'draft' && o.table_id === tableId && o.status === 'pending',
  )
  return found ?? entry
}

export async function enqueueTableSettle(
  tableId: string,
  payload: TableSettlePayload,
  afterDraftId?: string,
  scope: OfflineCacheScope = {},
): Promise<TableSettleOp> {
  const queue = await getTableOpsQueue(scope)
  const entry = enrichOp<TableSettleOp>({
    op: 'settle',
    id: newOpId(),
    table_id: tableId,
    payload: {
      ...payload,
      payment_lines: normalizePaymentLines(payload.payment_lines),
    },
    after_draft_id: afterDraftId,
    status: 'pending',
    enqueued_at: new Date().toISOString(),
  })

  let replaced = false
  for (let i = queue.length - 1; i >= 0; i--) {
    const item = queue[i]
    if (item.op === 'settle' && item.table_id === tableId && item.status === 'pending') {
      queue[i] = { ...entry, id: item.id, after_draft_id: afterDraftId ?? item.after_draft_id }
      replaced = true
      break
    }
  }
  if (!replaced) queue.push(entry)

  await saveTableOpsQueue(queue, scope)
  const saved = await getTableOpsQueue(scope)
  const found = [...saved].reverse().find(
    (o): o is TableSettleOp => o.op === 'settle' && o.table_id === tableId && o.status === 'pending',
  )
  return found ?? entry
}

/** Recover ops left in `syncing` after a crash or hard refresh. */
export async function resetStuckSyncingOps(scope: OfflineCacheScope = {}): Promise<number> {
  const queue = await getTableOpsQueue(scope)
  let reset = 0
  const next = queue.map((op) => {
    if (op.status !== 'syncing') return op
    reset += 1
    return { ...op, status: 'pending' as const }
  })
  if (reset > 0) await saveTableOpsQueue(next, scope)
  return reset
}

export async function enqueueTableTransfer(
  sourceId: string,
  targetId: string,
  scope: OfflineCacheScope = {},
  options?: { enqueuedWhileOffline?: boolean },
): Promise<TableTransferOp> {
  const queue = await getTableOpsQueue(scope)
  const entry = enrichOp<TableTransferOp>({
    op: 'transfer',
    id: newOpId(),
    source_id: sourceId,
    target_id: targetId,
    enqueued_while_offline: options?.enqueuedWhileOffline === true,
    status: 'pending',
    enqueued_at: new Date().toISOString(),
  })
  let replaced = false
  for (let i = queue.length - 1; i >= 0; i--) {
    const item = queue[i]
    if (
      item.op === 'transfer' &&
      item.source_id === sourceId &&
      item.target_id === targetId &&
      item.status === 'pending'
    ) {
      queue[i] = { ...entry, id: item.id }
      replaced = true
      break
    }
  }
  if (!replaced) queue.push(entry)
  await saveTableOpsQueue(queue, scope)
  const saved = await getTableOpsQueue(scope)
  return (
    [...saved].reverse().find(
      (o): o is TableTransferOp =>
        o.op === 'transfer' &&
        o.source_id === sourceId &&
        o.target_id === targetId &&
        o.status === 'pending',
    ) ?? entry
  )
}

export async function enqueueTableMerge(
  sourceId: string,
  targetId: string,
  scope: OfflineCacheScope = {},
  options?: { enqueuedWhileOffline?: boolean },
): Promise<TableMergeOp> {
  const queue = await getTableOpsQueue(scope)
  const entry = enrichOp<TableMergeOp>({
    op: 'merge',
    id: newOpId(),
    source_id: sourceId,
    target_id: targetId,
    enqueued_while_offline: options?.enqueuedWhileOffline === true,
    status: 'pending',
    enqueued_at: new Date().toISOString(),
  })
  let replaced = false
  for (let i = queue.length - 1; i >= 0; i--) {
    const item = queue[i]
    if (
      item.op === 'merge' &&
      item.source_id === sourceId &&
      item.target_id === targetId &&
      item.status === 'pending'
    ) {
      queue[i] = { ...entry, id: item.id }
      replaced = true
      break
    }
  }
  if (!replaced) queue.push(entry)
  await saveTableOpsQueue(queue, scope)
  const saved = await getTableOpsQueue(scope)
  return (
    [...saved].reverse().find(
      (o): o is TableMergeOp =>
        o.op === 'merge' &&
        o.source_id === sourceId &&
        o.target_id === targetId &&
        o.status === 'pending',
    ) ?? entry
  )
}

export async function enqueueTableUnmerge(
  targetId: string,
  sourceId: string,
  scope: OfflineCacheScope = {},
): Promise<TableUnmergeOp> {
  const queue = await getTableOpsQueue(scope)
  const entry = enrichOp<TableUnmergeOp>({
    op: 'unmerge',
    id: newOpId(),
    target_id: targetId,
    source_id: sourceId,
    status: 'pending',
    enqueued_at: new Date().toISOString(),
  })
  queue.push(entry)
  await saveTableOpsQueue(queue, scope)
  return entry
}

export async function enqueueTableRelease(
  tableId: string,
  scope: OfflineCacheScope = {},
): Promise<TableReleaseOp> {
  const queue = await getTableOpsQueue(scope)
  const entry = enrichOp<TableReleaseOp>({
    op: 'release',
    id: newOpId(),
    table_id: tableId,
    status: 'pending',
    enqueued_at: new Date().toISOString(),
  })
  queue.push(entry)
  await saveTableOpsQueue(queue, scope)
  return entry
}

export async function updateTableOpInQueue(
  opId: string,
  patch: Partial<Pick<TableOp, 'status' | 'last_error' | 'retry_count'>>,
  scope: OfflineCacheScope = {},
): Promise<void> {
  const queue = await getTableOpsQueue(scope)
  const idx = queue.findIndex((o) => o.id === opId)
  if (idx < 0) return
  queue[idx] = { ...queue[idx], ...patch } as TableOp
  await saveTableOpsQueue(queue, scope)
}

export async function removeTableOpById(opId: string, scope: OfflineCacheScope = {}): Promise<void> {
  const queue = await getTableOpsQueue(scope)
  await saveTableOpsQueue(
    queue.filter((o) => o.id !== opId),
    scope,
  )
}

export async function getPendingTableOpsCount(scope: OfflineCacheScope = {}): Promise<number> {
  const queue = await getTableOpsQueue(scope)
  return queue.filter((o) => o.status === 'pending' || o.status === 'syncing').length
}

export async function getFailedTableOps(scope: OfflineCacheScope = {}): Promise<FailedTableOp[]> {
  const raw = await idb.getItem<FailedTableOp[]>(failedKey(scope))
  return Array.isArray(raw) ? raw : []
}

export async function appendFailedTableOp(
  op: TableOp,
  reason: string,
  scope: OfflineCacheScope = {},
): Promise<void> {
  const list = await getFailedTableOps(scope)
  const now = new Date().toISOString()
  const idx = list.findIndex((f) => f.op.id === op.id)
  if (idx >= 0) {
    const copy = [...list]
    copy[idx] = {
      ...copy[idx],
      op,
      reason,
      lastAttemptAt: now,
      retryCount: (copy[idx].retryCount ?? 0) + 1,
    }
    await idb.setItem(failedKey(scope), copy)
  } else {
    await idb.setItem(failedKey(scope), [
      ...list,
      { op, reason, failedAt: now, lastAttemptAt: now, retryCount: 1 },
    ])
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pos:table-ops-updated'))
  }
}

export async function removeFailedTableOpById(opId: string, scope: OfflineCacheScope = {}): Promise<void> {
  const list = await getFailedTableOps(scope)
  const next = list.filter((f) => f.op.id !== opId)
  if (next.length === list.length) return
  await idb.setItem(failedKey(scope), next)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pos:table-ops-updated'))
  }
}

export async function moveFailedTableOpToPending(opId: string, scope: OfflineCacheScope = {}): Promise<boolean> {
  const failed = await getFailedTableOps(scope)
  const entry = failed.find((f) => f.op.id === opId)
  if (!entry) return false
  const queue = await getTableOpsQueue(scope)
  if (queue.some((o) => o.id === opId)) return false

  const restored: TableOp = { ...entry.op, status: 'pending', last_error: null }
  if (restored.op === 'draft') {
    await enqueueTableDraft(restored.table_id, restored.payload, scope, {
      enqueuedWhileOffline: restored.enqueued_while_offline,
    })
  } else {
    queue.push(restored)
    await saveTableOpsQueue(queue, scope)
  }
  await removeFailedTableOpById(opId, scope)
  return true
}

/** Last pending draft op id for a table (for settle dependency). */
export async function lastPendingDraftIdForTable(
  tableId: string,
  scope: OfflineCacheScope = {},
): Promise<string | null> {
  const queue = await getTableOpsQueue(scope)
  for (let i = queue.length - 1; i >= 0; i--) {
    const item = queue[i]
    if (item.op === 'draft' && item.table_id === tableId && (item.status === 'pending' || item.status === 'syncing')) {
      return item.id
    }
  }
  return null
}

/** Table ids involved in transfer / merge / unmerge. */
export function tableIdsForStructuralOp(op: TableOp): string[] {
  switch (op.op) {
    case 'transfer':
      return [op.source_id, op.target_id]
    case 'merge':
      return [op.source_id, op.target_id]
    case 'unmerge':
      return [op.source_id, op.target_id]
    default:
      return []
  }
}

export function isStructuralTableOp(op: TableOp): op is TableTransferOp | TableMergeOp | TableUnmergeOp {
  return op.op === 'transfer' || op.op === 'merge' || op.op === 'unmerge'
}

export function hasFailedDraftForTable(failed: FailedTableOp[], tableId: string): boolean {
  return failed.some((f) => f.op.op === 'draft' && f.op.table_id === tableId)
}

export function failedDraftTableIds(failed: FailedTableOp[]): Set<string> {
  const ids = new Set<string>()
  for (const f of failed) {
    if (f.op.op === 'draft') ids.add(f.op.table_id)
  }
  return ids
}

/** Pure: whether settle must wait (pending draft, syncing draft, or failed draft for table). */
export function settleDependsOnDraft(
  op: TableSettleOp,
  queue: TableOp[],
  failedDraftIds?: Set<string>,
): boolean {
  if (failedDraftIds?.has(op.table_id)) return true
  if (op.after_draft_id) {
    const linked = queue.find((q) => q.id === op.after_draft_id)
    if (linked && linked.status !== 'synced') return true
    if (!linked && failedDraftIds?.has(op.table_id)) return true
  }
  return queue.some(
    (q) =>
      q.op === 'draft' &&
      q.table_id === op.table_id &&
      (q.status === 'pending' || q.status === 'syncing'),
  )
}

/** Pure: transfer/merge/unmerge waits until drafts for involved tables leave the queue. */
export function structuralOpDependsOnDraft(op: TableOp, queue: TableOp[]): boolean {
  const tableIds = tableIdsForStructuralOp(op)
  if (tableIds.length === 0) return false
  return tableIds.some((tid) =>
    queue.some(
      (q) => q.op === 'draft' && q.table_id === tid && (q.status === 'pending' || q.status === 'syncing'),
    ),
  )
}

export function structuralOpBlockedByFailedDraft(op: TableOp, failedDraftIds: Set<string>): boolean {
  return tableIdsForStructuralOp(op).some((tid) => failedDraftIds.has(tid))
}

/** Tables with pending/syncing ops (for UI badges). */
export async function getPendingSyncTableIds(scope: OfflineCacheScope = {}): Promise<Set<string>> {
  const queue = await getTableOpsQueue(scope)
  const ids = new Set<string>()
  for (const o of queue) {
    if (o.status !== 'pending' && o.status !== 'syncing') continue
    switch (o.op) {
      case 'draft':
      case 'settle':
      case 'release':
        ids.add(o.table_id)
        break
      case 'transfer':
        ids.add(o.source_id)
        ids.add(o.target_id)
        break
      case 'merge':
        ids.add(o.source_id)
        ids.add(o.target_id)
        break
      case 'unmerge':
        ids.add(o.source_id)
        ids.add(o.target_id)
        break
      default:
        break
    }
  }
  return ids
}
